import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, type PanInfo } from "motion/react";
import { Check, X } from "lucide-react";
import { currencySymbol, formatRelativeTime } from "../lib/format";
import { usePendingMutation } from "../lib/hooks/usePendingMutation";
import { useReceiptCancelMutation } from "../lib/hooks/useReceiptCancelMutation";
import { PendingOut } from "../lib/schemas";
import { Avatar } from "../ui/Avatar";
import { Money } from "../ui/Money";

interface Props {
  pending: PendingOut;
  currentTgId: number;
  showChatTitle?: boolean;
}

const VERBS: Record<string, string> = {
  expense_quorum: "paid",
  paid_quorum: "paid for you",
  received_consent: "claims you paid them",
  settle_consent: "sent you a settlement",
};

const OWN_VERBS: Record<string, string> = {
  expense_quorum: "paid — waiting for approval",
  paid_quorum: "paid — waiting for approval",
  received_consent: "claimed payment — waiting for confirmation",
  settle_consent: "sent settlement — waiting for confirmation",
};

function stripHtml(text: string): string {
  return text.replace(/<\/?[^>]+>/g, "");
}

export default function PendingCard({ pending, currentTgId, showChatTitle = true }: Props) {
  const navigate = useNavigate();
  const x = useMotionValue(0);
  // Prevent click-navigation on the receipt card after a real drag
  const draggedRef = useRef(false);
  const mutation = usePendingMutation();
  const receiptCancelMutation = useReceiptCancelMutation();

  const isOwnPending = currentTgId === pending.sender_tg_id;
  const isSettleAll = pending.action_type === "settleall_multi_confirm";
  const isReceiptSettle = pending.action_type === "receipt_settle";
  const hasStructured = !isSettleAll && !isReceiptSettle && pending.amount != null && pending.currency != null;

  // Hint labels fade in smoothly as the card is dragged
  const leftHintOpacity = useTransform(x, [-60, -20], [1, 0]);
  const rightHintOpacity = useTransform(x, [20, 60], [0, 1]);

  // Left-only for own items; two-way for approver items
  const dragConstraints = isOwnPending ? { left: -80, right: 0 } : { left: -80, right: 80 };

  function doAction(action: "approve" | "deny" | "cancel") {
    mutation.mutate({ token: pending.token, action });
  }

  function cancelReceipt() {
    receiptCancelMutation.mutate({
      chatId: pending.chat.id,
      receiptId: pending.receipt_id ?? "",
      token: pending.token,
    });
  }

  function onDragEnd(_evt: unknown, info: PanInfo) {
    if (Math.abs(info.offset.x) > 5) {
      draggedRef.current = true;
      setTimeout(() => { draggedRef.current = false; }, 0);
    }
    if (isReceiptSettle && isOwnPending) {
      if (info.offset.x < -60) void cancelReceipt();
    } else if (isOwnPending) {
      if (info.offset.x < -60) void doAction("cancel");
    } else {
      if (info.offset.x > 60) void doAction("approve");
      else if (info.offset.x < -60) void doAction("deny");
    }
  }

  const actorName = isOwnPending ? "You" : (pending.actor_display ?? "Someone");
  const verb = isOwnPending
    ? (OWN_VERBS[pending.action_type] ?? "pending")
    : (VERBS[pending.action_type] ?? "requested action");

  // Receipt settle card — uploader can swipe-cancel; others get tap-only
  if (isReceiptSettle) {
    const receiptPath = `/groups/${pending.chat.id}/receipts/${pending.receipt_id ?? ""}`;
    return (
      <div className="relative mb-2">
        {isOwnPending && (
          <div className="absolute inset-0 flex items-center justify-end px-4 pointer-events-none">
            <motion.span
              style={{ opacity: leftHintOpacity }}
              className="flex items-center gap-1 text-danger text-sm font-semibold"
            >
              <X size={14} /> Cancel
            </motion.span>
          </div>
        )}
        <motion.div
          drag={isOwnPending ? "x" : false}
          style={{ x }}
          dragConstraints={dragConstraints}
          dragElastic={0.15}
          dragSnapToOrigin
          onDragEnd={onDragEnd}
          className="bg-surface border border-hairline rounded-2xl shadow-card p-4 select-none touch-pan-y"
          onClick={() => { if (!draggedRef.current) navigate(receiptPath); }}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-2xl">🧾</span>
              <div className="min-w-0">
                <p className="text-fg font-semibold text-sm leading-tight truncate">
                  Receipt to split
                </p>
                <p className="text-muted text-xs leading-tight">
                  {pending.actor_display ?? "Someone"} uploaded
                </p>
              </div>
            </div>
            {showChatTitle && (
              <span className="text-muted text-xs shrink-0 mt-0.5">{pending.chat.title}</span>
            )}
          </div>
          {pending.description && (
            <p className="text-fg text-sm font-medium mb-1">&ldquo;{pending.description}&rdquo;</p>
          )}
          {pending.amount && pending.currency && (
            <Money
              amount={pending.amount}
              currency={currencySymbol(pending.currency)}
              size="xl"
              className="block mb-1"
            />
          )}
          <p className="text-faint text-xs">
            Expires {formatRelativeTime(pending.expires_at)}
          </p>
          <div className="flex border-t border-hairline mt-3 -mx-4 -mb-4">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(receiptPath);
              }}
              className="flex-1 flex items-center justify-center py-3.5 rounded-b-2xl font-sans text-sm font-semibold text-fg active:bg-surface-2 transition-colors"
            >
              Open receipt →
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative mb-2">
      {/* Swipe hints — fade in smoothly as the card moves */}
      <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
        {isOwnPending ? (
          <motion.span
            style={{ opacity: leftHintOpacity }}
            className="flex items-center gap-1 text-danger text-sm font-semibold ml-auto"
          >
            <X size={14} /> Cancel
          </motion.span>
        ) : (
          <>
            <motion.span
              style={{ opacity: rightHintOpacity }}
              className="flex items-center gap-1 text-success text-sm font-semibold"
            >
              <Check size={14} /> Approve
            </motion.span>
            <motion.span
              style={{ opacity: leftHintOpacity }}
              className="flex items-center gap-1 text-danger text-sm font-semibold"
            >
              <X size={14} /> Deny
            </motion.span>
          </>
        )}
      </div>

      {/* Card */}
      <motion.div
        drag="x"
        style={{ x }}
        dragConstraints={dragConstraints}
        dragElastic={0.15}
        dragSnapToOrigin
        onDragEnd={onDragEnd}
        className="bg-surface border border-hairline rounded-2xl shadow-card p-4 select-none touch-pan-y"
      >
        {hasStructured ? (
          <>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <Avatar name={actorName} size="sm" />
                <div className="min-w-0">
                  <p className="text-fg font-semibold text-sm leading-tight truncate">
                    {actorName}
                  </p>
                  <p className="text-muted text-xs leading-tight">{verb}</p>
                </div>
              </div>
              {showChatTitle && (
                <span className="text-muted text-xs shrink-0 mt-0.5">{pending.chat.title}</span>
              )}
            </div>

            <Money
              amount={pending.amount!}
              currency={currencySymbol(pending.currency!)}
              size="xl"
              className="block mb-1"
            />

            {pending.description && (
              <p className="text-muted text-sm italic mb-3">
                for &ldquo;{pending.description}&rdquo;
              </p>
            )}

            <p className="text-faint text-xs">
              Expires {formatRelativeTime(pending.expires_at)}
            </p>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-fg font-medium text-sm whitespace-pre-line">
                {stripHtml(pending.summary)}
              </span>
              {showChatTitle && (
                <span className="text-muted text-xs shrink-0">{pending.chat.title}</span>
              )}
            </div>
            <p className="text-faint text-xs mb-3">
              Expires {formatRelativeTime(pending.expires_at)}
            </p>
          </>
        )}

        {/* Footer buttons */}
        <div className="flex border-t border-hairline mt-3 -mx-4 -mb-4">
          {isOwnPending ? (
            <button
              type="button"
              onClick={() => doAction("cancel")}
              className="flex-1 flex items-center justify-center py-3.5 rounded-b-2xl font-sans text-sm font-semibold text-muted active:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => doAction("deny")}
                className="flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-bl-2xl font-sans text-sm font-semibold text-danger border-r border-hairline active:bg-surface-2 transition-colors"
              >
                <X size={14} /> Deny
              </button>
              <button
                type="button"
                onClick={() => doAction("approve")}
                className="flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-br-2xl font-sans text-sm font-semibold text-fg active:bg-surface-2 transition-colors"
              >
                <Check size={14} /> Approve
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
